import { merge, mergeWith, omit, set } from 'lodash';
import { DateTime } from 'luxon';
import { nanoid } from 'nanoid';
import { proxy } from 'valtio';
import {
  addObjectFile,
  deletePermit,
  getPermit,
  getProject,
  type PermitFormDataPayload,
  type PermitMapView,
  upsertPermit,
} from '~/api/projects2';
import { compareWithArrayCopy } from '~/common/storeUtils';
import {
  EditorMode,
  LoadingState,
  type MapDetail,
  type MapObject,
  MapStyle,
  type Project,
  type TLoadedState,
} from '~/common/types';
import { CivilDrawStore } from '../civildraw/store';
import type { Contractor, PermittingFormValues } from './permittingFormTypes';
import { PERMITTING_LOCAL_MAP_ID } from './permittingMapConstants';

export { PERMITTING_LOCAL_MAP_ID } from './permittingMapConstants';

function localAuthor() {
  return { name: 'Permit draft' as const };
}

function createEmptyMapDetail(): MapDetail & TLoadedState {
  const now = DateTime.now();
  return {
    isLoading: false,
    id: PERMITTING_LOCAL_MAP_ID,
    name: 'Permit area',
    image: '',
    unitSystem: 'imperial',
    baseMap: MapStyle.civilGridLight,
    createdAt: now,
    updatedAt: now,
    createdBy: localAuthor(),
    updatedBy: localAuthor(),
    objects: {},
  };
}

function revokeBlobUrlsFromMap(map: MapDetail & TLoadedState) {
  for (const obj of Object.values(map.objects)) {
    for (const f of obj.files ?? []) {
      if (f.path.startsWith('blob:')) {
        URL.revokeObjectURL(f.path);
      }
    }
  }
}

/**
 * CivilDraw-compatible store for the permitting PDF embed. The map stays on the synthetic
 * `PERMITTING_LOCAL_MAP_ID` and all edits are in-memory, but the draft (form + objects + map view) is
 * persisted to the DB via `saveDraft`/`deleteDraft` and hydrated once per project via `loadDraft`. The
 * real DB ids live on `permitId`/`permitMapId` so the in-memory drawing behavior is unchanged.
 */
export class PermittingDrawStore extends CivilDrawStore {
  /** DB ids once the draft has been saved or loaded (the map keeps the sentinel id for drawing). */
  permitId?: string;
  permitMapId?: string;
  /** Saved map viewport, hydrated from the draft; applied by the embed on load. */
  mapView?: PermitMapView;
  /** Form values from the saved draft (null = no saved draft; undefined = not fetched yet). */
  loadedFormValues?: { formData: PermitFormDataPayload; contractors: Contractor[] } | null;
  /** Live in-memory form state, mirrored by the form so it survives tab switches (incl. unsaved edits). */
  sessionFormValues?: PermittingFormValues;
  /** True once the form has hydrated for this project session (defaults/draft applied). */
  formHydrated = false;
  /** Guards against re-fetching the draft (and clobbering unsaved edits) on tab remount. */
  private draftLoadedForProjectId?: string;

  override async loadProject(projectId?: string) {
    const targetProjectId = projectId || this.projectId;
    // Preserve the in-memory session across projectDetails tab remounts: only load once per project.
    if (targetProjectId && this.draftLoadedForProjectId === targetProjectId && !this.currentMap.isLoading) {
      return;
    }

    this.projectId = targetProjectId;
    this.project = LoadingState;
    this.undoStack = [];
    this.redoStack = [];
    this.allObjectIds = [];
    // A different project (or first load): re-hydrate the form from scratch and drop the previous
    // project's permit so nothing stale is read while this project's draft loads. `loadedFormValues`
    // must be cleared to `undefined` so the form's wait-guard holds until loadDraft resolves.
    this.formHydrated = false;
    this.sessionFormValues = undefined;
    this.loadedFormValues = undefined;
    this.permitId = undefined;
    this.permitMapId = undefined;
    this.mapView = undefined;
    this.resetCurrentObjectId();

    const project = (await getProject<Project>(this.projectId)) as Project;
    this.project = {
      ...project,
      maps: {},
      isLoading: false,
    } as Project & TLoadedState;

    this.currentMapId = PERMITTING_LOCAL_MAP_ID;
    await this.loadDraft();
    this.draftLoadedForProjectId = this.projectId;
  }

  /** Hydrates the saved permit draft (objects + map view + form values) if one exists. */
  async loadDraft() {
    const emptyMap = createEmptyMapDetail();

    let draft = null;
    try {
      draft = this.projectId ? await getPermit(this.projectId) : null;
    } catch {
      draft = null;
    }

    if (draft) {
      this.permitId = draft.id;
      this.permitMapId = draft.mapId;
      this.mapView = draft.mapView ?? undefined;
      this.loadedFormValues = { formData: draft.formData, contractors: draft.contractors };
      if (draft.map) {
        emptyMap.objects = { ...draft.map.objects };
      }
      this.currentMap = emptyMap;
      this.allObjectIds = Object.keys(emptyMap.objects);
    } else {
      this.permitId = undefined;
      this.permitMapId = undefined;
      this.mapView = undefined;
      this.loadedFormValues = null;
      this.currentMap = emptyMap;
    }
  }

  /**
   * Persists the draft: upserts the permit + its map, reconciles the drawn objects, then flushes any
   * locally-drawn image files (blob: URLs) to S3. Returns the saved draft.
   */
  async saveDraft(input: {
    formData: PermitFormDataPayload;
    contractors: Contractor[];
    mapView: PermitMapView | null;
  }) {
    if (!this.projectId || this.currentMap.isLoading) return null;

    const objects = Object.values(this.currentMap.objects);
    const draft = await upsertPermit(this.projectId, {
      formData: input.formData,
      contractors: input.contractors,
      mapView: input.mapView,
      objects,
    });

    this.permitId = draft.id;
    this.permitMapId = draft.mapId;
    this.mapView = draft.mapView ?? input.mapView ?? undefined;
    this.loadedFormValues = { formData: draft.formData, contractors: draft.contractors };

    // Upload any locally-drawn image files now that the objects exist on a real map.
    const uploadedFiles = await this.flushLocalObjectFiles(draft.mapId);

    // Swap in-memory blob URLs for the persisted (presigned) S3 URLs.
    if (uploadedFiles) {
      try {
        const refreshed = await getPermit(this.projectId);
        if (refreshed?.map && !this.currentMap.isLoading) {
          this.currentMap.objects = { ...refreshed.map.objects };
        }
      } catch {
        // Keep the in-memory blob URLs if the refresh fails; they still render this session.
      }
    }

    return draft;
  }

  /** Archives the draft server-side and wipes the local session (drawings, ids, view). */
  async deleteDraft() {
    if (this.projectId) {
      try {
        await deletePermit(this.projectId);
      } catch (e) {
        console.error('Failed to delete permit draft', e);
      }
    }
    this.permitId = undefined;
    this.permitMapId = undefined;
    this.mapView = undefined;
    this.loadedFormValues = null;
    this.clearPermitAreaDrawings();
  }

  /** Uploads image objects whose files are still local blob: URLs. Returns true if anything uploaded. */
  private async flushLocalObjectFiles(mapId: string): Promise<boolean> {
    if (this.currentMap.isLoading) return false;
    let uploaded = false;
    for (const obj of Object.values(this.currentMap.objects)) {
      for (const f of obj.files ?? []) {
        if (!f.path.startsWith('blob:')) continue;
        try {
          const blob = await (await fetch(f.path)).blob();
          const fd = new FormData();
          fd.append('file', blob, f.name || 'image');
          fd.append('name', f.name || 'image');
          fd.append('type', f.type || 'media');
          fd.append('extension', f.extension || '');
          await addObjectFile({ projectId: this.projectId, mapId, objectId: obj.id, file: fd });
          uploaded = true;
        } catch (e) {
          console.error('Failed to upload permit object file', e);
        }
      }
    }
    return uploaded;
  }

  override async setCurrentMapId(mapId: string) {
    if (mapId === PERMITTING_LOCAL_MAP_ID) {
      this.currentMapId = mapId;
      if (this.currentMap.isLoading) {
        this.currentMap = createEmptyMapDetail();
      }
      return;
    }
  }

  override async newMap(_name: string | null): Promise<string> {
    return PERMITTING_LOCAL_MAP_ID;
  }

  override async deleteMap(_mapId: string) {}

  override async duplicateMap(_mapId: string): Promise<string> {
    return PERMITTING_LOCAL_MAP_ID;
  }

  override async newObject(o: Omit<MapObject, 'createdBy' | 'updatedBy'>, setCurrentObject = true) {
    if (this.currentMap.isLoading) return;

    const now = DateTime.now();
    const id = o.id || nanoid();
    const author = localAuthor();
    const newObject = {
      ...o,
      id,
      createdAt: now,
      updatedAt: now,
      createdBy: author,
      updatedBy: author,
    } as MapObject;

    set(this.currentMap, `objects.${id}`, newObject);

    if (setCurrentObject) {
      this.setEditorMode(EditorMode.None);
      setTimeout(() => {
        this.setCurrentObjectId(id);
      }, 100);
    }
    return newObject;
  }

  override async addManyObjects(objs: Omit<MapObject[], 'createdBy' | 'updatedBy'>) {
    if (this.currentMap.isLoading) return;
    const raw = objs as unknown as Omit<MapObject, 'createdBy' | 'updatedBy'>[];
    const created: MapObject[] = [];
    for (const o of raw) {
      const obj = await this.newObject(o, false);
      if (obj) created.push(obj);
    }
    return created;
  }

  override async updateObject<T = MapObject>(o: Partial<T>) {
    if (!this.currentMap.isLoading && this.currentObjectId && this.currentObject) {
      const merged = mergeWith(this.currentObject, o, compareWithArrayCopy);
      this.currentMap.objects[this.currentObjectId] = merged;
      this.addObjectToUndoStack({ isNewUpdate: true });
    }
  }

  override async deleteObject<T = MapObject>(o: T, shouldPushToUndoStack = false) {
    if (!this.currentMap.isLoading) {
      delete this.currentMap.objects[(o as MapObject).id];
      if (shouldPushToUndoStack) {
        this.undoStack.push([this.previousObject, undefined]);
        this.redoStack = [];
      }
    }
    return undefined;
  }

  override async refreshMap() {}

  override async updateMap(m: Partial<MapDetail>) {
    if (!this.currentMap.isLoading) {
      this.currentMap = merge(this.currentMap, omit(m, 'baseMap')) as MapDetail & TLoadedState;
      this.currentMap.baseMap = MapStyle.civilGridLight;
    }
  }

  override async updateMapName(_mapId: string, _newName: string) {}

  override async restoreObject({ list, format }: { list: (string | undefined)[]; format: 'undo' | 'redo' }) {
    if (this.currentMap.isLoading) return;

    // Callers (undo/redo) always push a `[before, after]` pair: added = [undefined, obj],
    // updated = [before, after], deleted = [before, undefined]. Branch on definedness so TypeScript
    // narrows each object to a MapObject (no non-null assertions needed).
    const [before, after] = list.map((item) => {
      if (item === undefined) return undefined;
      const previous = JSON.parse(item);
      return {
        ...previous,
        createdAt: DateTime.fromISO(previous.createdAt, { outputCalendar: 'unix' }),
        updatedAt: DateTime.fromISO(previous.updatedAt, { outputCalendar: 'unix' }),
      } as MapObject;
    });

    if (before && after) {
      // Updated: undo restores the previous object, redo re-applies the newer one.
      const object = format === 'undo' ? before : after;
      Reflect.set(this.currentMap.objects, object.id, object);
    } else if (after && !before) {
      // Added: undo removes it, redo puts it back.
      if (format === 'undo') {
        await this.deleteObject(after);
        Reflect.deleteProperty(this.currentMap.objects, after.id);
      } else {
        Reflect.set(this.currentMap.objects, after.id, after);
      }
    } else if (before && !after) {
      // Deleted: undo puts it back, redo removes it again.
      if (format === 'undo') {
        Reflect.set(this.currentMap.objects, before.id, before);
      } else {
        await this.deleteObject(before);
        Reflect.deleteProperty(this.currentMap.objects, before.id);
      }
    }
    this.refreshMap();
  }

  override async addComment(_comment: string) {}

  override async archiveComment(_commentId: string) {}

  /**
   * Clears in-memory permit-area drawings while keeping the project loaded.
   * Used by `deleteDraft` after the permit is archived.
   */
  clearPermitAreaDrawings() {
    if (this.currentMap.isLoading) return;
    revokeBlobUrlsFromMap(this.currentMap);
    this.currentMap.objects = {};
    this.resetCurrentObjectId();
    this.setEditorMode(EditorMode.None);
    this.undoStack = [];
    this.redoStack = [];
    this.allObjectIds = [];
    this.previousObject = undefined;
    this.shouldRedraw = true;
  }
}

const permittingDrawStore = proxy(new PermittingDrawStore());
export default permittingDrawStore;
